/**
 * DataGrid: a jQuery-free replacement for the DataTables 2 setups used in the views.
 *
 * Server mode speaks the DataTables/yajra protocol (draw, start, length, search[value],
 * order[i][…], columns[i][…]) and expects { draw, recordsTotal, recordsFiltered, data }.
 * Client mode (no `ajax`) sorts, searches and pages the rows already in the <tbody>.
 *
 * Markup and class names follow DataTables 2's Bootstrap 5 renderer
 * (dt-container, dt-length, dt-search, dt-info, dt-paging, dt-column-order …),
 * so the DataTables stylesheet keeps the look identical.
 *
 * Supported options: ajax (string | { url, data(params) }), serverSide, processing, searching,
 * ordering, order, paging / paginate / bPaginate, lengthChange, pageLength, lengthMenu, info,
 * columns [{ data, name, render(data, type, row, meta), orderable, searchable, visible, className }],
 * drawCallback(settings).
 */

// DataTables' default `thousands: ','` in the info line (14,318).
const thousands = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
// DataTables 2 says "1 entry" but "2 entries".
const entries = (n) => (Number(n) === 1 ? 'entry' : 'entries');

const LANG = {
    lengthMenu: 'entries per page',
    search: 'Search:',
    info: (start, end, total) => `Showing ${thousands(start)} to ${thousands(end)} of ${thousands(total)} ${entries(total)}`,
    infoEmpty: 'Showing 0 to 0 of 0 entries',
    infoFiltered: (max) => ` (filtered from ${thousands(max)} total ${entries(max)})`,
    emptyTable: 'No data available in table',
    zeroRecords: 'No matching records found',
    processing: 'Processing...',
    paginate: { first: '«', previous: '‹', next: '›', last: '»' },
};

let instanceCounter = 0;

function getPath(obj, path) {
    if (path == null || obj == null) return undefined;
    if (Object.prototype.hasOwnProperty.call(obj, path)) return obj[path];
    return String(path)
        .split('.')
        .reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
}

function textOf(html) {
    if (html == null) return '';
    const s = String(html);
    if (!s.includes('<')) return s;
    const div = document.createElement('div');
    div.innerHTML = s;
    return div.textContent || '';
}

function toNumber(text) {
    const cleaned = String(text).replace(/(^\s*Rp\.?\s*)|[,\s]/gi, '');
    if (cleaned === '' || !/^-?\d*\.?\d+$/.test(cleaned)) return null;
    return parseFloat(cleaned);
}

function range(from, to) {
    const out = [];
    for (let i = from; i < to; i++) out.push(i);
    return out;
}

/** DataTables' page-number algorithm (7 buttons with ellipses). */
export function pageNumbers(page, pages, buttons = 7) {
    const half = Math.floor(buttons / 2);
    if (pages <= buttons) return range(0, pages);
    if (page <= half) return [...range(0, buttons - 2), 'ellipsis', pages - 1];
    if (page >= pages - 1 - half) return [0, 'ellipsis', ...range(pages - (buttons - 2), pages)];
    return [0, 'ellipsis', ...range(page - half + 2, page + half - 1), 'ellipsis', pages - 1];
}

/** Build the query DataTables sends for a server-side draw. */
export function buildServerParams({ draw, start, length, search, order, columns }) {
    const p = new URLSearchParams();
    p.append('draw', draw);
    columns.forEach((c, i) => {
        p.append(`columns[${i}][data]`, c.data ?? '');
        p.append(`columns[${i}][name]`, c.name ?? '');
        p.append(`columns[${i}][searchable]`, String(c.searchable));
        p.append(`columns[${i}][orderable]`, String(c.orderable));
        p.append(`columns[${i}][search][value]`, '');
        p.append(`columns[${i}][search][regex]`, 'false');
    });
    order.forEach(([col, dir], i) => {
        p.append(`order[${i}][column]`, col);
        p.append(`order[${i}][dir]`, dir);
        p.append(`order[${i}][name]`, columns[col]?.name ?? '');
    });
    p.append('start', start);
    p.append('length', length);
    p.append('search[value]', search);
    p.append('search[regex]', 'false');
    p.append('_', Date.now());
    return p;
}

export class DataGrid {
    constructor(table, options = {}) {
        this.table = typeof table === 'string' ? document.querySelector(table) : table;
        if (!this.table) throw new Error(`DataGrid: table ${table} not found`);
        if (this.table._dataGrid) return this.table._dataGrid;
        this.table._dataGrid = this;

        const o = options;
        this.id = this.table.id || `dt-${++instanceCounter}`;
        this.serverSide = !!o.serverSide && !!o.ajax;
        this.ajaxOpt = o.ajax || null;
        this.showProcessing = o.processing ?? false;
        this.searching = o.searching ?? true;
        this.ordering = o.ordering ?? true;
        this.paging = o.paging ?? o.paginate ?? o.bPaginate ?? true;
        this.lengthChange = o.lengthChange ?? true;
        this.info = o.info ?? true;
        this.lengthMenu = o.lengthMenu || [10, 25, 50, 100];
        this.pageLength = o.pageLength || this.lengthMenu[0] || 10;
        this.drawCallback = o.drawCallback || null;
        this.order = (o.order ?? [[0, 'asc']]).map(([c, d]) => [c, String(d || 'asc').toLowerCase()]);

        this.page = 0;
        this.search = '';
        this.drawCount = 0;
        this.rows = [];
        this.recordsTotal = 0;
        this.recordsFiltered = 0;

        this.headerCells = Array.from(this.table.querySelectorAll('thead > tr:last-child > th, thead > tr:last-child > td'));
        this.columns = this.normalizeColumns(o.columns);

        // Public API parity with DataTables: dTable.ajax.reload(), dTable.ajax.url(u).load()
        this.ajax = {
            reload: (callback, resetPaging = true) => this.reload(callback, resetPaging),
            url: (url) => {
                this.ajaxOpt = typeof this.ajaxOpt === 'object' && this.ajaxOpt ? { ...this.ajaxOpt, url } : url;
                return { load: (cb, reset = true) => this.reload(cb, reset) };
            },
        };

        if (!this.serverSide) this.readDomRows();
        this.render();
        if (this.serverSide || this.ajaxOpt) this.fetchData();
        else this.draw();
    }

    normalizeColumns(cols) {
        const count = Math.max(cols ? cols.length : 0, this.headerCells.length);
        return range(0, count).map((i) => {
            const c = (cols && cols[i]) || {};
            return {
                data: c.data === undefined ? (cols ? null : i) : c.data,
                name: c.name ?? '',
                render: c.render || null,
                orderable: c.orderable ?? true,
                searchable: c.searchable ?? true,
                visible: c.visible ?? true,
                className: c.className || '',
            };
        });
    }

    readDomRows() {
        const body = this.table.tBodies[0];
        this.domRows = body ? Array.from(body.rows) : [];
        this.domRows.forEach((tr, idx) => {
            tr._dgIndex = idx;
            tr._dgText = Array.from(tr.cells).map((td) => td.textContent.trim());
        });
    }

    // ---------------------------------------------------------------- layout

    render() {
        const t = this.table;
        t.classList.add('dataTable');
        this.renderHeader();

        const container = document.createElement('div');
        container.className = 'dt-container dt-bootstrap5';
        container.id = `${this.id}_wrapper`;
        t.parentNode.insertBefore(container, t);

        const cell = 'd-md-flex justify-content-between align-items-center';
        const top = document.createElement('div');
        top.className = 'row mt-2 justify-content-between';
        top.innerHTML = `<div class="${cell} dt-layout-start col-md-auto me-auto"></div><div class="${cell} dt-layout-end col-md-auto ms-auto"></div>`;
        const middle = document.createElement('div');
        middle.className = 'row mt-2 justify-content-md-center';
        middle.innerHTML = '<div class="col-12 dt-layout-full col-md"></div>';
        const bottom = document.createElement('div');
        bottom.className = 'row mt-2 justify-content-between';
        bottom.innerHTML = `<div class="${cell} dt-layout-start col-md-auto me-auto"></div><div class="${cell} dt-layout-end col-md-auto ms-auto"></div>`;

        if (this.lengthChange && this.paging) top.children[0].appendChild(this.buildLength());
        if (this.searching) top.children[1].appendChild(this.buildSearch());
        middle.firstElementChild.appendChild(t);
        if (this.info) {
            this.infoEl = document.createElement('div');
            this.infoEl.className = 'dt-info';
            this.infoEl.setAttribute('aria-live', 'polite');
            this.infoEl.setAttribute('role', 'status');
            this.infoEl.id = `${this.id}_info`;
            bottom.children[0].appendChild(this.infoEl);
        }
        if (this.paging) {
            this.pagingEl = document.createElement('div');
            this.pagingEl.className = 'dt-paging';
            bottom.children[1].appendChild(this.pagingEl);
            this.pagingEl.addEventListener('click', (e) => this.onPageClick(e));
        }

        if (top.querySelector('.dt-length, .dt-search')) container.appendChild(top);
        container.appendChild(middle);
        container.appendChild(bottom);

        this.processingEl = document.createElement('div');
        this.processingEl.className = 'dt-processing card';
        this.processingEl.setAttribute('role', 'status');
        this.processingEl.style.display = 'none';
        this.processingEl.innerHTML = `${LANG.processing}<div><div></div><div></div><div></div><div></div></div>`;
        container.appendChild(this.processingEl);

        this.container = container;
        if (!t.tBodies[0]) t.appendChild(document.createElement('tbody'));
    }

    renderHeader() {
        this.columns.forEach((col, i) => {
            const th = this.headerCells[i];
            if (!th) return;
            if (!col.visible) {
                th.style.display = 'none';
                return;
            }
            if (col.className) th.classList.add(...col.className.split(/\s+/).filter(Boolean));
            if (!th.querySelector('.dt-column-title')) {
                const title = document.createElement('span');
                title.className = 'dt-column-title';
                while (th.firstChild) title.appendChild(th.firstChild);
                th.appendChild(title);
                const orderIcon = document.createElement('span');
                orderIcon.className = 'dt-column-order';
                th.appendChild(orderIcon);
            }
            if (this.ordering && col.orderable) {
                th.classList.add('dt-orderable-asc', 'dt-orderable-desc');
                th.tabIndex = 0;
                th.addEventListener('click', () => this.toggleOrder(i));
                th.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter') this.toggleOrder(i);
                });
            }
        });
        this.updateHeaderState();
    }

    updateHeaderState() {
        this.headerCells.forEach((th, i) => {
            th.classList.remove('dt-ordering-asc', 'dt-ordering-desc');
            th.removeAttribute('aria-sort');
            const o = this.ordering && this.order.find(([c]) => c === i);
            if (o) {
                th.classList.add(`dt-ordering-${o[1]}`);
                th.setAttribute('aria-sort', o[1] === 'asc' ? 'ascending' : 'descending');
            }
        });
    }

    buildLength() {
        const wrap = document.createElement('div');
        wrap.className = 'dt-length';
        const id = `dt-length-${instanceCounter}-${this.id}`;
        const options = this.lengthMenu.map((n) => `<option value="${n}"${n === this.pageLength ? ' selected' : ''}>${n === -1 ? 'All' : n}</option>`).join('');
        wrap.innerHTML = `<select name="${this.id}_length" aria-controls="${this.id}" class="form-select form-select-sm" id="${id}">${options}</select><label for="${id}"> ${LANG.lengthMenu}</label>`;
        wrap.querySelector('select').addEventListener('change', (e) => {
            this.pageLength = parseInt(e.target.value, 10);
            this.page = 0;
            this.refresh();
        });
        return wrap;
    }

    buildSearch() {
        const wrap = document.createElement('div');
        wrap.className = 'dt-search';
        const id = `dt-search-${instanceCounter}-${this.id}`;
        wrap.innerHTML = `<label for="${id}">${LANG.search}</label><input type="search" class="form-control form-control-sm" id="${id}" aria-controls="${this.id}">`;
        const input = wrap.querySelector('input');
        let timer;
        input.addEventListener('input', () => {
            clearTimeout(timer);
            const apply = () => {
                if (input.value === this.search) return;
                this.search = input.value;
                this.page = 0;
                this.refresh();
            };
            // DataTables throttles server-side searching (400ms) and filters client-side immediately.
            if (this.serverSide) timer = setTimeout(apply, 400);
            else apply();
        });
        return wrap;
    }

    // ---------------------------------------------------------------- behaviour

    toggleOrder(col) {
        const current = this.order.length && this.order[0][0] === col ? this.order[0][1] : null;
        this.order = [[col, current === 'asc' ? 'desc' : 'asc']];
        this.page = 0;
        this.updateHeaderState();
        this.refresh();
    }

    onPageClick(e) {
        const btn = e.target.closest('[data-dt-idx]');
        if (!btn || btn.closest('.disabled')) return;
        e.preventDefault();
        const pages = this.pageCount();
        const idx = btn.dataset.dtIdx;
        const target = { first: 0, previous: this.page - 1, next: this.page + 1, last: pages - 1 }[idx] ?? parseInt(idx, 10);
        if (Number.isNaN(target) || target < 0 || target >= pages || target === this.page) return;
        this.page = target;
        this.refresh();
    }

    pageCount() {
        if (!this.paging || this.pageLength === -1) return 1;
        return Math.max(1, Math.ceil(this.recordsFiltered / this.pageLength));
    }

    refresh() {
        if (this.serverSide) this.fetchData();
        else this.draw();
    }

    reload(callback, resetPaging = true) {
        if (resetPaging) this.page = 0;
        const done = this.serverSide || this.ajaxOpt ? this.fetchData() : Promise.resolve(this.draw());
        return Promise.resolve(done).then(() => callback && callback(this.rows));
    }

    ajaxUrl() {
        return typeof this.ajaxOpt === 'string' ? this.ajaxOpt : this.ajaxOpt?.url;
    }

    setProcessing(on) {
        if (this.showProcessing) this.processingEl.style.display = on ? 'block' : 'none';
    }

    async fetchData() {
        const draw = ++this.drawCount;
        const params = this.serverSide
            ? buildServerParams({
                draw,
                start: this.paging ? this.page * this.pageLength : 0,
                length: this.paging ? this.pageLength : -1,
                search: this.search,
                order: this.ordering ? this.order : [],
                columns: this.columns,
            })
            : new URLSearchParams();
        if (this.ajaxOpt && typeof this.ajaxOpt === 'object' && typeof this.ajaxOpt.data === 'function') {
            const extra = {};
            const ret = this.ajaxOpt.data(extra);
            Object.entries(ret || extra).forEach(([k, v]) => params.set(k, v));
        }
        const url = this.ajaxUrl();
        this.setProcessing(true);
        try {
            const res = await fetch(url + (url.includes('?') ? '&' : '?') + params.toString(), {
                credentials: 'same-origin',
                headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json, text/javascript, */*; q=0.01' },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            if (draw !== this.drawCount) return; // a newer request superseded this one
            if (this.serverSide) {
                this.rows = json.data || [];
                this.recordsTotal = json.recordsTotal ?? this.rows.length;
                this.recordsFiltered = json.recordsFiltered ?? this.rows.length;
                this.draw();
            } else {
                // Client-side data loaded over ajax: keep raw rows and process locally.
                this.clientData = Array.isArray(json) ? json : json.data || [];
                this.draw();
            }
        } catch (err) {
            if (draw !== this.drawCount) return;
            console.error(`DataGrid (${this.id}):`, err);
            this.rows = [];
            this.recordsTotal = this.recordsFiltered = 0;
            this.draw();
        } finally {
            if (draw === this.drawCount) this.setProcessing(false);
        }
    }

    cellValue(row, col, rowIdx, colIdx) {
        const value = col.data == null ? null : getPath(row, col.data);
        if (col.render) return col.render(value, 'display', row, { row: rowIdx, col: colIdx, settings: this });
        return value ?? '';
    }

    buildRow(row, rowIdx) {
        const tr = document.createElement('tr');
        if (row.DT_RowId) tr.id = row.DT_RowId;
        if (row.DT_RowClass) tr.className = row.DT_RowClass;
        if (row.DT_RowAttr) Object.entries(row.DT_RowAttr).forEach(([k, v]) => tr.setAttribute(k, v));
        this.columns.forEach((col, i) => {
            if (!col.visible) return;
            const td = document.createElement('td');
            if (col.className) td.className = col.className;
            const v = this.cellValue(row, col, rowIdx, i);
            td.innerHTML = v == null ? '' : String(v);
            tr.appendChild(td);
        });
        return tr;
    }

    emptyRow(message) {
        const tr = document.createElement('tr');
        const td = document.createElement('td');
        td.className = 'dt-empty';
        td.colSpan = this.columns.filter((c) => c.visible).length || 1;
        td.textContent = message;
        tr.appendChild(td);
        return tr;
    }

    /** Client mode: returns the rows to show after search, sort and paging. */
    processClient() {
        const fromData = Array.isArray(this.clientData);
        let items = fromData
            ? this.clientData.map((row, idx) => ({
                row,
                idx,
                text: this.columns.map((c, i) => textOf(this.cellValue(row, c, idx, i))),
            }))
            : this.domRows.map((tr) => ({ tr, idx: tr._dgIndex, text: tr._dgText }));

        this.recordsTotal = items.length;
        const terms = this.search.toLowerCase().split(/\s+/).filter(Boolean);
        if (terms.length) {
            items = items.filter((it) => {
                const hay = it.text.filter((_, i) => this.columns[i]?.searchable !== false).join(' ').toLowerCase();
                return terms.every((t) => hay.includes(t));
            });
        }
        this.recordsFiltered = items.length;

        if (this.ordering && this.order.length) {
            const [col, dir] = this.order[0];
            const numeric = items.every((it) => (it.text[col] ?? '') === '' || toNumber(it.text[col]) !== null);
            const sign = dir === 'desc' ? -1 : 1;
            items.sort((a, b) => {
                const av = a.text[col] ?? '';
                const bv = b.text[col] ?? '';
                let cmp;
                if (numeric) cmp = (toNumber(av) ?? -Infinity) - (toNumber(bv) ?? -Infinity);
                else cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
                return cmp * sign || a.idx - b.idx;
            });
        }

        if (this.paging && this.pageLength !== -1) {
            const pages = Math.max(1, Math.ceil(items.length / this.pageLength));
            if (this.page >= pages) this.page = pages - 1;
            items = items.slice(this.page * this.pageLength, (this.page + 1) * this.pageLength);
        }
        return items;
    }

    draw() {
        const body = this.table.tBodies[0];
        const frag = document.createDocumentFragment();
        let shown;

        if (this.serverSide) {
            shown = this.rows.length;
            this.rows.forEach((row, i) => frag.appendChild(this.buildRow(row, i)));
        } else {
            const items = this.processClient();
            shown = items.length;
            items.forEach((it) => frag.appendChild(it.tr || this.buildRow(it.row, it.idx)));
        }

        body.replaceChildren();
        if (!shown) frag.appendChild(this.emptyRow(this.recordsTotal ? LANG.zeroRecords : LANG.emptyTable));
        body.appendChild(frag);

        this.renderInfo(shown);
        this.renderPaging();
        if (this.drawCallback) this.drawCallback.call(this, this);
        this.table.dispatchEvent(new CustomEvent('draw.dt', { detail: this }));
    }

    renderInfo(shown) {
        if (!this.infoEl) return;
        if (!this.recordsFiltered) {
            this.infoEl.textContent = LANG.infoEmpty + (this.recordsTotal ? LANG.infoFiltered(this.recordsTotal) : '');
            return;
        }
        const start = this.paging && this.pageLength !== -1 ? this.page * this.pageLength + 1 : 1;
        const end = start + shown - 1;
        let text = LANG.info(start, end, this.recordsFiltered);
        if (this.recordsFiltered !== this.recordsTotal) text += LANG.infoFiltered(this.recordsTotal);
        this.infoEl.textContent = text;
    }

    renderPaging() {
        if (!this.pagingEl) return;
        const pages = this.pageCount();
        const page = this.page;
        const btn = (idx, label, { disabled = false, active = false, cls = '' } = {}) =>
            `<li class="dt-paging-button page-item${disabled ? ' disabled' : ''}${active ? ' active' : ''}">` +
            `<button class="page-link${cls ? ' ' + cls : ''}" type="button" role="link" aria-controls="${this.id}"` +
            `${disabled ? ' aria-disabled="true" tabindex="-1"' : ''}${active ? ' aria-current="page"' : ''} data-dt-idx="${idx}">${label}</button></li>`;
        const first = page === 0;
        const last = page >= pages - 1;
        let html = btn('first', LANG.paginate.first, { disabled: first, cls: 'first' });
        html += btn('previous', LANG.paginate.previous, { disabled: first, cls: 'previous' });
        // DataTables shows no page numbers at all when there are no records.
        (this.recordsFiltered > 0 ? pageNumbers(page, pages) : []).forEach((n) => {
            html += n === 'ellipsis'
                ? '<li class="dt-paging-button page-item disabled"><a class="page-link ellipsis" aria-disabled="true" tabindex="-1">…</a></li>'
                : btn(n, n + 1, { active: n === page });
        });
        html += btn('next', LANG.paginate.next, { disabled: last, cls: 'next' });
        html += btn('last', LANG.paginate.last, { disabled: last, cls: 'last' });
        this.pagingEl.innerHTML = `<nav aria-label="pagination"><ul class="pagination">${html}</ul></nav>`;
    }

    destroy() {
        if (!this.container) return;
        this.container.parentNode.insertBefore(this.table, this.container);
        this.container.remove();
        this.table.classList.remove('dataTable');
        delete this.table._dataGrid;
    }
}
